import { DateTime } from "luxon";
import roleConfig from "../../common/layout/roleConfig";
import { estGroupe, filtrerMenu, privilegesMasques } from "../../common/layout/MenuLateral/visibiliteMenu";

// ============================================================
// Tableau de bord — fonctions pures : sections de raccourcis (mêmes entrées,
// même ordre et même filtrage par rôle que le menu latéral), salutation,
// date du jour, interprétation du terme de recherche rapide.
// ============================================================

// Une ligne par entrée du menu : ce qu'on y fait, dans les mots des agents.
export const DESCRIPTIONS = {
  "/rdvs": "Planning des rendez-vous des allocataires, traitement et reports.",
  "/all-recipients": "Recherche par nom, NIR ou matricule dans toutes les collections.",
  "/recipients": "Allocataires suivis : fiches, parcours, RDO et rendez-vous.",
  "/recipients-temp": "Dossiers créés manuellement, en attente de rapprochement.",
  "/recipients-stock": "Allocataires importés du stock, à orienter.",
  "/stats": "Indicateurs d'activité : orientations, rendez-vous, EP.",
  "/reorientations": "Demandes de réorientation entre organismes, à traiter.",
  "/users": "Comptes des agents : rôles, organisations et sites.",
  "/organizations": "Organismes partenaires et leurs paramètres.",
  "/sites": "Sites d'accueil, boxes et calendriers de créneaux.",
  "/anomalies": "Toutes les anomalies détectées sur les dossiers.",
  "/my-anomalies": "Les anomalies qui vous sont assignées.",
  "/suivi-anomalies": "BRSA soumis à droits et devoirs sans RDO ou sans RDV EG.",
  "/anomalies-orientation-ft": "Traitements France Travail en échec, à rejouer.",
  "/operateurs": "Toutes les structures du répertoire : associations, collectivités, services publics, entreprises.",
  "/operateurs/accompagnement": "Structures exerçant un régime d'accompagnement, leurs sites et calendriers.",
  "/operateurs/regles-sous-orientation": "Règles qui confient aux opérateurs les allocataires orientés vers la Caf.",
  "/themes": "Activités proposées par les opérateurs.",
  "/cer-data": "Objectifs, freins et étapes des contrats CER.",
  "/cer-list": "Contrats d'engagement réciproque, en cours ou signés.",
  "/sectors": "Secteurs géographiques des EP et leurs membres.",
  "/ep": "Sessions d'équipe pluridisciplinaire de l'année, par mois.",
  "/myeps": "Vos convocations comme membre du jury.",
  "/allepinvitations": "Suivi des invitations envoyées aux membres du jury.",
  "/sanctions-france-travail": "Allocataires orientés France Travail et autres référents uniques : sanctions échangées avec France Travail.",
  "/suspension-remobilisation": "Demandes à valider par le Directeur ou ses délégataires.",
  "/dynamic-constants": "Paramètres métier de la plateforme.",
  "/simulator": "Simulation d'orientation à partir d'un questionnaire RDO.",
  "/file-exchange": "Import et export de fichiers plats.",
  "/invite-rdo": "Envoi d'invitations à renseigner la RDO.",
  "/imports": "Suivi et rejeu des imports de fichiers.",
  "/exports": "Suivi des exports demandés et téléchargement.",
};

export const SECTION_ACCES_DIRECTS = { id: "acces-directs", name: "Accès directs", icon: "bolt" };

const slug = (texte) =>
  String(texte || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const enrichir = (item) => ({ ...item, description: DESCRIPTIONS[item.path] || "" });

// Sections du tableau de bord : les entrées directes du menu d'abord, puis
// une section par groupe, avec le filtrage par rôle du menu (isEP,
// directorOnly, roleConfig). Les groupes vides disparaissent.
export function sectionsDuTableauDeBord(items = [], user = {}) {
  const visibles = filtrerMenu(items, user, privilegesMasques(user, roleConfig));
  const sections = [];
  const directes = visibles.filter((item) => !estGroupe(item));
  if (directes.length) sections.push({ ...SECTION_ACCES_DIRECTS, entrees: directes.map(enrichir) });
  visibles.filter(estGroupe).forEach((groupe) => {
    sections.push({ id: slug(groupe.name), name: groupe.name, icon: groupe.icon, entrees: groupe.items.map(enrichir) });
  });
  return sections;
}

export function estVisible(sections = [], path) {
  return sections.some((s) => s.entrees.some((e) => e.path === path));
}

const capitaliser = (texte) => (texte ? texte.charAt(0).toUpperCase() + texte.slice(1) : "");

export function prenomAffiche(user = {}) {
  const prenom = String(user.firstname || "").trim();
  return prenom ? capitaliser(prenom) : "";
}

export function salutation(user = {}) {
  const prenom = prenomAffiche(user);
  return prenom ? `Bonjour ${prenom}` : "Bonjour";
}

export function dateDuJour(maintenant = DateTime.local()) {
  return capitaliser(maintenant.setLocale("fr").toFormat("cccc d MMMM yyyy"));
}

// Recherche rapide : un terme tout en chiffres est un NIR (13 chiffres et
// plus) ou un matricule, sinon un nom de famille. Le résultat a la forme de
// l'atome de filtres de « Rechercher un allocataire ».
export function filtresDepuisTerme(terme) {
  const t = String(terme || "").trim();
  if (!t) return null;
  const base = { firstname: "", lastname: "", nir: "", CAFId: "" };
  const chiffres = t.replace(/\s/g, "");
  if (/^\d+$/.test(chiffres)) {
    return { filters: { ...base, [chiffres.length >= 13 ? "nir" : "CAFId"]: chiffres } };
  }
  return { filters: { ...base, lastname: t } };
}
